package com.arena_web.ui.components.overlay

import androidx.compose.runtime.*
import com.arena_web.data.dto.PersonDto
import com.arena_web.data.dto.isDeletedAccount
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.widgets.HiddenScrollbarStyle
import com.arena_web.ui.components.widgets.InlineSpinner
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.profile.mobile.openUserProfile
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.px

// =============================================================================
// Mobile "Interactions" bottom sheet — Figma 8289:3249 (overlay layer).
//
// The phone counterpart to the desktop [InteractionsModal], opened by tapping
// the heart action in the mobile top bar. Same data (peopleRepository
// .getInteractions → new likes + matches) and the same [PlayerCard] visuals,
// re-laid-out as a full-height bottom sheet with the twin halftone-dot
// flourishes + bottom-right diagonal-bar accent baked into the design.
// =============================================================================

private val BADGE_RED = Color.rgb(0xFF, 0x42, 0x42)
private val BADGE_GREEN = Color.rgb(0x2B, 0xAA, 0x1F)

private const val SECTION_DIVIDER_GRADIENT =
    "linear-gradient(90deg, " +
        "rgba(148, 149, 152, 0) 0%, " +
        "rgba(148, 149, 152, 0.27) 25%, " +
        "rgba(98, 99, 101, 0.3) 50%, " +
        "rgba(148, 149, 152, 0.27) 75%, " +
        "rgba(148, 149, 152, 0) 100%)"

// Halftone-dot + diagonal-bar artwork, shared 1:1 with the desktop modal.
private const val BG_TOPRIGHT_URL = "/images/interactions-bg-topright.png"
private const val BG_BOTTOMLEFT_URL = "/images/interactions-bg-bottomleft.png"
private const val BG_BOTTOMRIGHT_URL = "/images/interactions-bg-bottomright.png"

@Composable
fun MobileInteractionsSheet(
    sportName: String,
    onDismiss: () -> Unit,
) {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val sportColors = SportColors.forSport(sportName.lowercase())
    val t = LocalAppStrings.current

    var isLoading by remember { mutableStateOf(false) }
    var newLikes by remember { mutableStateOf<List<PersonDto>>(emptyList()) }
    var matches by remember { mutableStateOf<List<PersonDto>>(emptyList()) }

    // Load on mount; show the cached lists silently on a re-open.
    LaunchedEffect(Unit) {
        val repo = container.peopleRepository ?: return@LaunchedEffect
        val hadData = newLikes.isNotEmpty() || matches.isNotEmpty()
        if (!hadData) isLoading = true
        repo.getInteractions().onSuccess { payload ->
            newLikes = payload.newLikes
            matches = payload.matches
            container.updateInteractionsNewLikesCount(payload.newLikes.size)
        }
        isLoading = false
    }

    val onNewLikeClick: (PersonDto) -> Unit = { person ->
        if (!person.isDeletedAccount()) {
            container.setPendingFocusPerson(person)
            onDismiss()
            ctx.router.navigateTo("/people")
        }
    }
    val onMatchClick: (PersonDto) -> Unit = { person ->
        if (!person.isDeletedAccount()) {
            onDismiss()
            openUserProfile(ctx, person.userId)
        }
    }

    // sidePaddingPx = 0 → the panel is edge-to-edge, so the background art and
    // the card rows reach the screen edges; the header + sections re-add their
    // own m(16) inset below.
    BottomSheet(onDismiss = onDismiss, contentGap = 0, fullHeight = true, sidePaddingPx = 0) {
        // Edge-to-edge root. `overflow: hidden` + top radius clip the corner
        // flourishes to the sheet's rounded top corners.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("position", "relative")
                    property("overflow", "hidden")
                    property("border-top-left-radius", s(22))
                    property("border-top-right-radius", s(22))
                }
        ) {
            // ── Background flourishes (non-interactive). ──
            Box(modifier = Modifier.styleModifier {
                property("position", "absolute"); property("top", "0"); property("right", "0")
                property("width", s(150)); property("height", s(320))
                property("background", "url('$BG_TOPRIGHT_URL') top right / 100% 100% no-repeat")
                property("opacity", "var(--l-deco-opacity, 1)")
                property("pointer-events", "none")
            })
            Box(modifier = Modifier.styleModifier {
                property("position", "absolute"); property("bottom", "0"); property("left", "0")
                property("width", s(165)); property("height", s(320))
                property("background", "url('$BG_BOTTOMLEFT_URL') bottom left / 100% 100% no-repeat")
                property("opacity", "var(--l-deco-opacity, 1)")
                property("pointer-events", "none")
            })
            Box(modifier = Modifier.styleModifier {
                property("position", "absolute"); property("bottom", "0"); property("right", "0")
                property("width", s(300)); property("height", s(260))
                property("background", "url('$BG_BOTTOMRIGHT_URL') bottom right / 100% 100% no-repeat")
                property("opacity", "var(--l-deco-opacity, 1)")
                property("pointer-events", "none")
            })

            // Vertical scroll only. No side padding here — each child adds its
            // own m(16) inset, while the card rows stay full-width and scroll
            // horizontally on their own. overflow-x: hidden guarantees nothing
            // but the rows ever scrolls sideways.
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier {
                        property("position", "relative")
                        property("overflow-y", "auto")
                        property("overflow-x", "hidden")
                        property("scrollbar-width", "none")
                        property("box-sizing", "border-box")
                    }
            ) {
                // ── Header ──
                Header(
                    newLikesCount = newLikes.size,
                    matchesCount = matches.size,
                    subtitleColor = sportColors.primary,
                )

                Box(modifier = Modifier.sHeight(28))

                if (isLoading && newLikes.isEmpty() && matches.isEmpty()) {
                    Box(
                        modifier = Modifier.fillMaxWidth().sHeight(252),
                        contentAlignment = Alignment.Center,
                    ) { InlineSpinner(size = 40.px) }
                } else {
                    Section(
                        label = t.interactionsNewLikes,
                        count = newLikes.size,
                        badgeColor = BADGE_RED,
                        people = newLikes,
                        emptyIcon = AppIcons.EmptyLikes,
                        emptyTitle = t.interactionsNoNewLikesTitle,
                        emptyMessage = t.interactionsNoNewLikesMessage,
                        sportName = sportName,
                        onPersonClick = onNewLikeClick,
                    )

                    Box(modifier = Modifier.sHeight(40))

                    Section(
                        label = t.interactionsMatches,
                        count = matches.size,
                        badgeColor = BADGE_GREEN,
                        people = matches,
                        emptyIcon = AppIcons.EmptyMatches,
                        emptyTitle = t.interactionsNoMatchesTitle,
                        emptyMessage = t.interactionsNoMatchesMessage,
                        sportName = sportName,
                        onPersonClick = onMatchClick,
                    )
                }

                // Trailing spacer — keeps a comfortable bottom gap at scroll-end.
                Box(modifier = Modifier.fillMaxWidth().sHeight(24))
            }
        }
    }
}

@Composable
private fun Header(
    newLikesCount: Int,
    matchesCount: Int,
    subtitleColor: Color,
) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sGap(12)
            .styleModifier {
                property("padding-left", s(16))
                property("padding-right", s(16))
                property("box-sizing", "border-box")
            }
    ) {
        Column(modifier = Modifier.fillMaxWidth().sGap(8)) {
            SpanText(
                t.interactionsTitle,
                modifier = Modifier
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .sFontSize(24)
                    .sLineHeight(32)
                    .sLetterSpacing(0.24)
                    .fontWeight(AppTypography.Medium)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
            )
            SpanText(
                t.interactionsSubtitle(newLikesCount, matchesCount),
                modifier = Modifier
                    .color(subtitleColor)
                    .sFontSize(16)
                    .sLineHeight(24)
                    .sLetterSpacing(0.16)
                    .fontWeight(AppTypography.Regular)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
            )
        }
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(1.px)
                .styleModifier {
                    property("background", SECTION_DIVIDER_GRADIENT)
                    property("border-radius", "1px")
                }
        )
    }
}

@Composable
private fun Section(
    label: String,
    count: Int,
    badgeColor: Color,
    people: List<PersonDto>,
    emptyIcon: String,
    emptyTitle: String,
    emptyMessage: String,
    sportName: String,
    onPersonClick: (PersonDto) -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier
                .sHeight(24)
                .styleModifier {
                    property("padding-left", s(16))
                    property("padding-right", s(16))
                    property("box-sizing", "border-box")
                },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                label,
                modifier = Modifier
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .sFontSize(18)
                    .sLineHeight(22)
                    .sLetterSpacing(0.09)
                    .fontWeight(AppTypography.Medium)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
            )
            Box(modifier = Modifier.sWidth(12))
            Box(
                modifier = Modifier
                    .sSize(24)
                    .styleModifier {
                        property("border-radius", s(100))
                        property("background", "#${badgeColor.toHexString()}")
                    },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    count.toString(),
                    modifier = Modifier
                        .color(AppColors.PrimaryWhite)
                        .sFontSize(14)
                        .sLineHeight(16)
                        .fontWeight(AppTypography.Medium)
                        .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                        .styleModifier { property("transform", "translateY(1px)") }
                )
            }
        }

        Box(modifier = Modifier.sHeight(20))

        if (people.isEmpty()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(204)
                    .styleModifier {
                        property("padding-left", s(16))
                        property("padding-right", s(16))
                        property("box-sizing", "border-box")
                    },
                contentAlignment = Alignment.Center,
            ) {
                EmptyState(icon = emptyIcon, title = emptyTitle, message = emptyMessage)
            }
        } else {
            // Full-width row (= panel width, edge-to-edge). Inner padding keeps
            // the first / last card at the 16px inset; overflow-x: auto scrolls
            // ONLY this row. No negative margins → nothing leaks to the page.
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("gap", s(16))
                        property("overflow-x", "auto")
                        property("align-items", "center")
                        property("padding-left", s(16))
                        property("padding-right", s(16))
                        property("box-sizing", "border-box")
                    }
                    .then(HiddenScrollbarStyle.toModifier()),
            ) {
                people.forEach { person ->
                    Box(
                        modifier = Modifier
                            .styleModifier { property("flex-shrink", "0") }
                            .cursor(Cursor.Pointer)
                            .onClick { onPersonClick(person) },
                    ) {
                        PlayerCard(
                            playerName = person.fullName.takeIf { it.isNotBlank() } ?: person.username,
                            rating = 0,
                            position = "",
                            sport = sportName,
                            size = PlayerCardSize.Small,
                            avatarUrl = person.avatarUrl,
                            cardAssetsBasePath = person.cardAssetsBasePath,
                            cardTextColor = person.cardTextColor,
                            hideRating = true,
                            hideShadow = false,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun EmptyState(icon: String, title: String, message: String) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("align-items", "center")
                property("justify-content", "center")
                property("gap", s(20))
                property("text-align", "center")
            },
    ) {
        Icon(src = icon)
        Column(modifier = Modifier.sGap(10), horizontalAlignment = Alignment.CenterHorizontally) {
            SpanText(
                title,
                modifier = Modifier
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .sFontSize(18)
                    .sLineHeight(22)
                    .fontWeight(AppTypography.Medium)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
            )
            Box(modifier = Modifier.sMaxWidth(300)) {
                SpanText(
                    message,
                    modifier = Modifier
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .sFontSize(14)
                        .sLineHeight(20)
                        .fontWeight(AppTypography.Regular)
                        .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                )
            }
        }
    }
}

private fun Color.toHexString(): String {
    val rgb = toRgb()
    fun Int.hex2(): String = toString(16).padStart(2, '0')
    return "${rgb.red.hex2()}${rgb.green.hex2()}${rgb.blue.hex2()}"
}
